import { useEffect } from "react";
import PostItem from "./PostItem";
import { useInfinitePosts } from "../hooks/use-infinite-posts";
import { useInfiniteScroll } from "../hooks/use-infinite-scroll";
import { PostListSkeleton } from "./skeletons";
import type { Post } from "../types/post";

interface PostListProps {
  readonly userId?: number; // If provided, only shows posts from this user
  readonly currentUserId?: number; // Current user's ID for permission checks
  readonly showActions?: boolean; // Whether to show edit/delete actions
  readonly isAdmin?: boolean; // Whether current user is admin
  readonly onEdit?: (post: Post) => void;
  readonly onDelete?: (postId: number) => void;
  readonly onMakePremium?: (postId: number) => void;
  readonly onRefetchReady?: (refetch: () => void) => void; // Callback to expose refetch function
}

export default function PostList({
  userId,
  currentUserId,
  showActions,
  isAdmin,
  onEdit,
  onDelete,
  onMakePremium,
  onRefetchReady,
}: PostListProps) {
  const { posts, loading, loadingMore, error, hasMore, loadMore, refetch } =
    useInfinitePosts(userId);

  useInfiniteScroll({
    hasMore,
    loading: loadingMore,
    onLoadMore: loadMore,
    threshold: 100,
  });

  // Expose refetch function to parent
  useEffect(() => {
    if (onRefetchReady) {
      onRefetchReady(refetch);
    }
  }, [refetch, onRefetchReady]);

  if (loading) {
    return <PostListSkeleton count={5} />;
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 dark:text-red-400 mb-2">
          <svg
            className="w-12 h-12 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
        <button
          onClick={() => globalThis.location.reload()}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 mb-4">
          <svg
            className="w-12 h-12 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
            />
          </svg>
        </div>
        <p className="text-gray-500 dark:text-gray-400">
          {userId
            ? "No posts yet. Create your first post!"
            : "No posts in the feed yet."}
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200 dark:divide-gray-700">
      {posts.map((post) => (
        <PostItem
          key={post.id}
          post={post}
          currentUserId={currentUserId}
          showActions={showActions}
          isAdmin={isAdmin}
          onEdit={onEdit}
          onDelete={onDelete}
          onMakePremium={onMakePremium}
        />
      ))}

      {/* Loading more indicator */}
      {loadingMore && (
        <div className="py-8 text-center">
          <div className="inline-flex items-center space-x-2 text-gray-500 dark:text-gray-400">
            <svg
              className="animate-spin h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>Loading more posts...</span>
          </div>
        </div>
      )}

      {/* No more posts indicator */}
      {!hasMore && posts.length > 0 && !loading && (
        <div className="py-8 text-center">
          <div className="text-gray-500 dark:text-gray-400">
            <svg
              className="w-8 h-8 mx-auto mb-2 opacity-50"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
              />
            </svg>
            <p className="text-sm">No more posts</p>
          </div>
        </div>
      )}
    </div>
  );
}
